import {zodResolver} from "@hookform/resolvers/zod";
import type {CompanyType, ICompany, UpdateLegalIdentityPayload} from "@mytour/shared";
import {CompanyTypeArray, CompanyTypesWithCapital} from "@mytour/shared";
import {Splitter} from "@mytour/ui/layout";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Button, Form, FormFeedback, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateLegalIdentity} from "../../../query/companySettingsQueries";

type Props = {
  company: ICompany;
};

const identitySchema = z.object({
  legalName: z.string().min(1, "Champ obligatoire"),
  commercialName: z.string(),
  companyType: z.enum(CompanyTypeArray),
  companyCapital: z.string(),
});

type IdentityFormData = z.infer<typeof identitySchema>;

export function IdentitySubTab({company}: Props) {
  const {t: translate} = useTranslation();
  const {updateLegalIdentity, isUpdatingLegalIdentity} = useUpdateLegalIdentity();
  const disabled = isUpdatingLegalIdentity;

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: {errors},
  } = useForm<IdentityFormData>({
    resolver: zodResolver(identitySchema),
    defaultValues: {
      legalName: company.legal.legalName ?? "",
      commercialName: company.commercialName || "",
      /**
       * "" affiche l'option vide tant que companyType n'est pas déclaré ;
       * identitySchema (z.enum) la rejette à la soumission, jamais de fallback "EI".
       */
      companyType: (company.legal.companyType ?? "") as CompanyType,
      companyCapital: company.legal.companyCapital?.toString() ?? "",
    },
  });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup>
        <Label for="legalName">{translate("myTourApp.platformUser.company")} *</Label>
        <Input
          id="legalName"
          innerRef={register("legalName").ref}
          onChange={register("legalName").onChange}
          onBlur={register("legalName").onBlur}
          name="legalName"
          invalid={!!errors.legalName}
        />
        {errors.legalName && <FormFeedback>{errors.legalName.message}</FormFeedback>}
      </FormGroup>

      <FormGroup>
        <Label for="commercialName">Nom commercial (optionnel)</Label>
        <Input
          id="commercialName"
          placeholder="Ex: Dupont Pro"
          innerRef={register("commercialName").ref}
          onChange={register("commercialName").onChange}
          onBlur={register("commercialName").onBlur}
          name="commercialName"
        />
        <small className="form-text text-muted">
          Affiché sur la facture si renseigné (en complément de la raison sociale)
        </small>
      </FormGroup>

      <Splitter>
        <FormGroup>
          <Label for="companyType">{translate("myTourApp.platformUser.companyType")}</Label>
          <Input
            type="select"
            id="companyType"
            innerRef={register("companyType").ref}
            onChange={register("companyType").onChange}
            onBlur={register("companyType").onBlur}
            name="companyType"
            invalid={!!errors.companyType}
          >
            <option value="">--</option>
            {CompanyTypeArray.map((companyType) => (
              <option value={companyType} key={companyType}>
                {companyType}
              </option>
            ))}
          </Input>
          {errors.companyType && <FormFeedback>{errors.companyType.message}</FormFeedback>}
        </FormGroup>
        {CompanyTypesWithCapital.has(watch("companyType")) && (
          <FormGroup>
            <Label>{translate("myTourApp.platformUser.companyCapital")}</Label>
            <div className="inputEuro">
              <Controller
                name="companyCapital"
                control={control}
                render={({field}) => (
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === "" ? "" : val);
                    }}
                    type="number"
                    id="companyCapital"
                    step="1"
                    min="1"
                  />
                )}
              />
              <span style={{margin: "auto", color: "#333"}}>&nbsp;&euro;</span>
            </div>
          </FormGroup>
        )}
      </Splitter>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingLegalIdentity ? <Spinner size="sm" /> : null}
        {isUpdatingLegalIdentity ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function onSubmit(data: IdentityFormData) {
    const payload: UpdateLegalIdentityPayload = {
      commercialName: data.commercialName,
      legalName: data.legalName,
      companyType: data.companyType,
      companyCapital:
        CompanyTypesWithCapital.has(data.companyType) && data.companyCapital
          ? Number(data.companyCapital)
          : undefined,
    };

    try {
      await updateLegalIdentity(payload);
      toast.success("Identité enregistrée");
    } catch {
      toast.error("Erreur lors de la sauvegarde de l'identité");
    }
  }
}
